import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { 
  FaSave, FaTimes, FaEdit, FaBuilding, FaUser, FaPhone, FaGlobe, 
  FaMapMarkerAlt, FaCalendarAlt, FaIdCard, FaCheck, FaSpinner, 
  FaExclamationTriangle, FaUpload, FaEnvelope, FaIndustry, FaFileInvoice,
  FaInfoCircle, FaChevronRight, FaRegBuilding, FaWarehouse, FaBoxes, FaClone,
  FaClock
} from "react-icons/fa";

// Constants and theme configuration
const API_URL = "/api";

const THEME = {
  colors: {
    primary: '#384959',
    secondary: '#64748b',
    muted: '#A0AEC0',
    accent: '#6A89A7',
    border: '#E2E8F0',
    tabBorder: '#BDDDFC',
    background: '#F9FAFB',
    pageBackground: '#FFFFFF',
    cardHeader: '#F9FAFB',
    error: '#EF4444',
    success: '#10B981',
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }
};

// --- SearchableDropdown Component (As requested) ---
const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  isLoading = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle positioning
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    return () => { window.removeEventListener('resize', handleUpdatePosition); window.removeEventListener('scroll', handleUpdatePosition); };
  }, [isOpen]);

  // Scroll into view on highlight
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) items[highlightedIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => { searchInputRef.current.focus(); }, 0);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); return; } return; }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setHighlightedIndex(prev => { if (prev < filteredOptions.length - 1) return prev + 1; else if (filteredOptions.length > 0) return 0; return prev; }); break;
      case 'ArrowUp': e.preventDefault(); setHighlightedIndex(prev => { if (prev > 0) return prev - 1; else if (filteredOptions.length > 0) return filteredOptions.length - 1; return prev; }); break;
      case 'Enter': e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) handleSelectOption(filteredOptions[highlightedIndex]); break;
      case 'Escape': setIsOpen(false); break; default: break;
    }
  };

  const handleSelectOption = (option) => { onChange(getOptionValue(option)); setIsOpen(false); setSearchTerm(''); setHighlightedIndex(-1); };
  
  useEffect(() => { const handleClickOutside = (event) => { if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false); }; document.addEventListener('mousedown', handleClickOutside); return () => { document.removeEventListener('mousedown', handleClickOutside); }; }, []);
  
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center bg-white ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(226, 232, 240)' }}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate text-gray-700">
           {isLoading && <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2 inline-block"></div>}
          {selectedOption ? getOptionLabel(selectedOption) : <span className="text-gray-400">{placeholder}</span>}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-xl"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0] bg-gray-50">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search time zone..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#6A89A7]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto custom-scrollbar" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] border-b border-gray-100 last:border-0 ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF] font-medium' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="text-sm">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500 mt-0.5">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">No time zones found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
// -----------------------------------------------------

const INITIAL_FORM_DATA = {
  company_id: "C-001",
  company_name: "CRONUS IN",
  business_type: "Pvt Ltd",
  industry: "IT/Software",
  gst_number: "GSTIN123456789",
  pan_number: "ABCDE1234F",
  cin_number: "CIN123456789",
  registration_date: "2020-01-15",
  email: "info@cronus.in",
  phone_number: "+91 11 2345 6789",
  website: "www.cronus.in",
  address_line1: "The Ring 5, Patel Nagar",
  address_line2: "",
  city: "New Delhi",
  state: "Delhi",
  country: "India",
  pincode: "110001",
  contact_person: "Adam Matteson",
  allow_negative_inventory: false,
  allow_warehouse_negative_inventory: false,
  allow_duplicate_batch: false,
  time_zone_id: "India Standard Time" // Default updated to match SQL Server naming
};

const BUSINESS_TYPE_OPTIONS = [
  { value: "Sole Proprietorship", label: "Sole Proprietorship" },
  { value: "Partnership", label: "Partnership" },
  { value: "LLP", label: "LLP" },
  { value: "Pvt Ltd", label: "Pvt Ltd" },
  { value: "Public Ltd", label: "Public Ltd" },
  { value: "Other", label: "Other" }
];

const INDUSTRY_OPTIONS = [
  { value: "IT/Software", label: "IT/Software" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Education", label: "Education" },
  { value: "Retail", label: "Retail" },
  { value: "Finance", label: "Finance" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Other", label: "Other" }
];

// REMOVED: Browser-based time zone generation
// We will now fetch these from the API

const useCompanyData = (companyId) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCompany = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching company with ID: ${companyId}`);
      const response = await fetch(`${API_URL}/companies/${companyId}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log("Company not found, using default data");
          setCompany(INITIAL_FORM_DATA);
          return;
        }
        throw new Error(`Failed to fetch company information: ${response.status}`);
      }
      const data = await response.json();
      console.log("Company data received:", data);
      setCompany(data);
    } catch (error) {
      console.error("Error fetching company:", error);
      setError(error.message || "Failed to fetch company information");
      setCompany(INITIAL_FORM_DATA);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  return { company, loading, error, refetch: fetchCompany };
};

const FormField = ({ label, name, value, onChange, disabled, placeholder, icon, type = "text", options, required = false }) => {
  const { colors } = THEME;
  
  return (
    <div className="mb-6">
      <label className="block text-sm font-medium mb-2 flex items-center" style={{ color: colors.secondary }}>
        {icon && <span className="mr-2" style={{ color: colors.accent }}>{icon}</span>}
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {type === 'select' ? (
        <div className="relative">
          <select
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            className="w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2 transition-all disabled:bg-gray-50 appearance-none"
            style={{ 
              borderColor: colors.border,
              color: colors.primary,
              backgroundColor: disabled ? '#f9fafb' : colors.background,
              focusRingColor: colors.accent + '40'
            }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5" style={{ color: colors.muted }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
      ) : type === 'checkbox' ? (
        <div className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={value}
            onChange={onChange}
            disabled={disabled}
            className="h-5 w-5 rounded border focus:ring-2 transition-all"
            style={{ 
              borderColor: colors.border,
              focusRingColor: colors.accent + '40',
              color: colors.accent
            }}
          />
          <label className="ml-3 text-sm" style={{ color: colors.primary }}>
            {placeholder}
          </label>
        </div>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          required={required}
          className="w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2 transition-all disabled:bg-gray-50"
          style={{ 
            borderColor: colors.border,
            color: colors.primary,
            backgroundColor: disabled ? '#f9fafb' : colors.background,
            focusRingColor: colors.accent + '40'
          }}
        />
      )}
    </div>
  );
};

const SectionCard = ({ title, icon, description, children }) => {
  const { colors } = THEME;
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border mb-6 transition-all hover:shadow-md" style={{ borderColor: colors.border }}>
      <div className="p-5 border-b flex items-center" style={{ borderColor: colors.border }}>
        <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: colors.accent + '20' }}>
          {React.cloneElement(icon, { style: { color: colors.accent } })}
        </div>
        <div>
          <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>{title}</h3>
          {description && <p className="text-sm mt-1" style={{ color: colors.secondary }}>{description}</p>}
        </div>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  );
};

const CompanyHeader = ({ company, editMode, logoPreview, onLogoChange, onLogoRemove }) => {
  const { colors } = THEME;
  return (
    <div className="bg-gray-50 rounded-xl p-6 mb-8 border" style={{ borderColor: colors.border }}>
      <div className="flex flex-col md:flex-row items-center justify-between">
        <div className="flex items-center mb-4 md:mb-0">
          <div className="mr-5">
            {logoPreview ? (
              <img src={logoPreview} alt="Company Logo" className="h-20 w-20 object-contain border-2 rounded-xl p-3 bg-white shadow-sm" style={{ borderColor: colors.border }} />
            ) : (
              <div className="h-20 w-20 border-2 border-dashed rounded-xl flex flex-col items-center justify-center bg-white shadow-sm" >
                <FaRegBuilding className="text-2xl mb-1" style={{ color: colors.muted }} />
                <span className="text-xs" style={{ color: colors.muted }}>Logo</span>
              </div>
            )}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-black">{company.company_name}</h1>
            <div className="flex items-center mt-2">
              <span className="px-3 py-1 rounded-full text-sm font-medium mr-3 bg-teal-800 text-white">{company.business_type}</span>
              <span className="text-sm bg-teal-800 text-white rounded-full mr-3 px-3 py-1">{company.industry}</span>
            </div>
          </div>
        </div>
        {editMode && (
          <div className="flex flex-col items-center">
            <label className="cursor-pointer bg-white rounded-lg px-4 py-2 border flex items-center shadow-sm hover:shadow-md transition-all mb-2" style={{ borderColor: colors.border }}>
              <FaUpload className="mr-2" style={{ color: colors.accent }} />
              <span style={{ color: colors.primary }}>Upload Logo</span>
              <input type="file" className="hidden" onChange={onLogoChange} accept="image/*" />
            </label>
            <button onClick={onLogoRemove} className="text-sm font-medium transition-colors flex items-center" style={{ color: colors.muted }}>
              <FaTimes className="mr-1" /> Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const LoadingSpinner = () => {
  const { colors } = THEME;
  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar />
      <main className="flex-1 p-8 overflow-hidden ml-20 transition-all duration-300 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: colors.accent }}></div>
          <p className="mt-4" style={{ color: colors.secondary }}>Loading company information...</p>
        </div>
      </main>
    </div>
  );
};

const ErrorMessage = ({ message }) => {
  const { colors } = THEME;
  return (
    <div className="mb-6 p-4 rounded-lg border-l-4 flex items-start" style={{ backgroundColor: '#FEF2F2', borderColor: colors.error, color: colors.error }}>
      <FaExclamationTriangle className="mt-0.5 mr-3 flex-shrink-0" />
      <div><p className="font-medium">Error</p><p className="text-sm">{message}</p></div>
    </div>
  );
};

const SuccessNotification = ({ message, onClose }) => {
  const { colors } = THEME;
  useEffect(() => { const timer = setTimeout(() => onClose(), 3000); return () => clearTimeout(timer); }, [onClose]);
  return (
    <div className="fixed top-4 right-4 px-4 py-3 rounded-md shadow-lg z-50 flex items-center gap-2 animate-fade-in-down" style={{ backgroundColor: colors.success, color: 'white' }}>
      <FaCheck /> {message}
    </div>
  );
};

const CompanyMaster = () => {
  const { company_id = "C-001" } = useParams();
  const navigate = useNavigate();
  const { company, loading, error, refetch } = useCompanyData(company_id);
  
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [apiError, setApiError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  
  // --- NEW: Time Zone State ---
  const [timeZoneOptions, setTimeZoneOptions] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(false);

  // --- NEW: Fetch Time Zones from API ---
  useEffect(() => {
    const fetchTimeZones = async () => {
      setLoadingTimezones(true);
      try {
        const response = await fetch(`${API_URL}/timezones`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTimeZoneOptions(data);
        } else {
          console.error("Failed to fetch time zones");
        }
      } catch (err) {
        console.error("Error fetching time zones:", err);
      } finally {
        setLoadingTimezones(false);
      }
    };
    fetchTimeZones();
  }, []);
  
  // Initialize form data when company data is loaded
  useEffect(() => {
    if (company) {
      setFormData({
        company_id: company.CompanyID || "C-001",
        company_name: company.CompanyName || "CRONUS IN",
        business_type: company.BusinessType || "Pvt Ltd",
        industry: company.Industry || "IT/Software",
        gst_number: company.GSTNumber || "GSTIN123456789",
        pan_number: company.PANNumber || "ABCDE1234F",
        cin_number: company.CINNumber || "CIN123456789",
        registration_date: company.RegistrationDate ? company.RegistrationDate.split('T')[0] : "2020-01-15",
        email: company.Email || "info@cronus.in",
        phone_number: company.PhoneNumber || "+91 11 2345 6789",
        website: company.Website || "www.cronus.in",
        address_line1: company.AddressLine1 || "The Ring 5, Patel Nagar",
        address_line2: company.AddressLine2 || "",
        city: company.City || "New Delhi",
        state: company.State || "Delhi",
        country: company.Country || "India",
        pincode: company.Pincode || "110001",
        contact_person: company.ContactPerson || "Adam Matteson",
        allow_negative_inventory: company.AllowNegativeInventory || false,
        allow_warehouse_negative_inventory: company.AllowWarehouseNegativeInventory || false,
        allow_duplicate_batch: company.AllowDuplicateBatch || false,
        time_zone_id: company.TimeZoneId || "India Standard Time" // Updated default
      });
      
      if (company.CompanyLogo) {
        fetchLogo();
      }
    }
  }, [company]);
  
  const fetchLogo = async () => {
    try {
      const logoResponse = await fetch(`${API_URL}/companies/${company_id}/files/logo`,{headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }});
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoUrl = URL.createObjectURL(logoBlob);
        setLogoPreview(logoUrl);
      }
    } catch (logoError) {
      console.error("Error fetching logo:", logoError);
    }
  };
  
  const handleLogout = () => { navigate('/login'); };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };
  
  const handleLogoRemove = () => {
    setLogoPreview(null);
    setLogoFile(null);
  };
  
  const handleSave = async () => {
    setSaving(true);
    setApiError(null);
    try {
      console.log("Saving company data:", formData);
      const saveData = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'allow_negative_inventory' || key === 'allow_warehouse_negative_inventory' || key === 'allow_duplicate_batch') {
          saveData.append(key, formData[key] ? '1' : '0');
        } else {
          saveData.append(key, formData[key]);
        }
      });
      
      saveData.append('updated_by', '1');
      
      if (logoFile) {
        saveData.append('logo', logoFile);
      }
      
      const response = await fetch(`${API_URL}/companies/${company_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: saveData
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update company information: ${response.status}`);
      }
      
      setShowSaved(true);
      setEditMode(false);
      refetch();
    } catch (error) {
      console.error("Error updating company:", error);
      setApiError(error.message || "Failed to update company information");
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    if (company) {
      setFormData({
        company_id: company.CompanyID || "C-001",
        company_name: company.CompanyName || "CRONUS IN",
        business_type: company.BusinessType || "Pvt Ltd",
        industry: company.Industry || "IT/Software",
        gst_number: company.GSTNumber || "GSTIN123456789",
        pan_number: company.PANNumber || "ABCDE1234F",
        cin_number: company.CINNumber || "CIN123456789",
        registration_date: company.RegistrationDate ? company.RegistrationDate.split('T')[0] : "2020-01-15",
        email: company.Email || "info@cronus.in",
        phone_number: company.PhoneNumber || "+91 11 2345 6789",
        website: company.Website || "www.cronus.in",
        address_line1: company.AddressLine1 || "The Ring 5, Patel Nagar",
        address_line2: company.AddressLine2 || "",
        city: company.City || "New Delhi",
        state: company.State || "Delhi",
        country: company.Country || "India",
        pincode: company.Pincode || "110001",
        contact_person: company.ContactPerson || "Adam Matteson",
        allow_negative_inventory: company.AllowNegativeInventory || false,
        allow_warehouse_negative_inventory: company.AllowWarehouseNegativeInventory || false,
        allow_duplicate_batch: company.AllowDuplicateBatch || false,
        time_zone_id: company.TimeZoneId || "India Standard Time" // Updated default
      });
      
      if (company.CompanyLogo) { fetchLogo(); } else { setLogoPreview(null); }
      setLogoFile(null);
    }
    setEditMode(false);
    setApiError(null);
  };
  
  const { colors } = THEME;
  
  if (loading) { return <LoadingSpinner />; }
  
  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} onLogout={handleLogout} />
      <main className={`flex-1 p-8 overflow-hidden ml-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center" style={{ color: colors.primary }}>
                <FaBuilding className="mr-3" style={{ color: colors.accent }} />
                Company Profile
              </h1>
              <p className="mt-2 flex items-center" style={{ color: colors.secondary }}>
                <FaInfoCircle className="mr-2" /> Manage your company details and settings
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {editMode ? (
                <>
                  <button onClick={handleCancel} className="px-5 py-2.5 rounded-lg border flex items-center gap-2 transition-all hover:shadow-sm font-medium" style={{ borderColor: colors.border, color: colors.secondary, backgroundColor: colors.background }}>
                    <FaTimes /> Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 rounded-lg text-white flex items-center gap-2 transition-all hover:shadow-md disabled:opacity-50 font-medium" style={{ backgroundColor: colors.accent }}>
                    {saving ? <><FaSpinner className="animate-spin" /> Saving...</> : <><FaSave /> Save Changes</>}
                  </button>
                </>
              ) : (
                <button onClick={() => setEditMode(true)} className="px-5 py-2.5 rounded-lg text-white bg-teal-800 flex items-center gap-2 transition-all hover:shadow-md font-medium">
                  <FaEdit /> Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
        
        {apiError && <ErrorMessage message={apiError} />}
        {showSaved && <SuccessNotification message="Changes saved successfully!" onClose={() => setShowSaved(false)} />}
        
        <CompanyHeader company={formData} editMode={editMode} logoPreview={logoPreview} onLogoChange={handleLogoChange} onLogoRemove={handleLogoRemove} />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SectionCard title="General Information" icon={<FaBuilding />} description="Basic details about your company">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Company Name" name="company_name" value={formData.company_name.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter company name" icon={<FaBuilding />} required />
                <FormField label="Email Address" name="email" value={formData.email.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter email address" icon={<FaEnvelope />} type="email" required />
                <FormField label="Phone Number" name="phone_number" value={formData.phone_number} onChange={handleInputChange} disabled={!editMode} placeholder="Enter phone number" icon={<FaPhone />} required />
                <FormField label="Website" name="website" value={formData.website.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter website URL" icon={<FaGlobe />} />
                
                <FormField label="Business Type" name="business_type" value={formData.business_type?.toUpperCase() || ""} onChange={handleInputChange} disabled={!editMode} type="select" options={BUSINESS_TYPE_OPTIONS.map(opt => ({ ...opt, label: opt.label.toUpperCase(), }))} icon={<FaBuilding />} required />
                <FormField label="Industry" name="industry" value={formData.industry?.toUpperCase() || ""} onChange={handleInputChange} disabled={!editMode} type="select" options={INDUSTRY_OPTIONS.map(opt => ({ ...opt, label: opt.label.toUpperCase(), }))} icon={<FaIndustry />} required />
                <FormField label="GST Registration Number" name="gst_number" value={formData.gst_number.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter GST number" icon={<FaFileInvoice />} />
                
                {/* --- UPDATED TIME ZONE FIELD --- */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2 flex items-center" style={{ color: colors.secondary }}>
                    <FaClock className="mr-2" style={{ color: colors.accent }} />
                    Time Zone
                  </label>
                  <SearchableDropdown
                    options={timeZoneOptions}
                    value={formData.time_zone_id}
                    onChange={(val) => setFormData(prev => ({ ...prev, time_zone_id: val }))}
                    placeholder="Search time zone..."
                    isLoading={loadingTimezones}
                    disabled={!editMode}
                  />
                </div>
              </div>
            </SectionCard>
            
            {/* Address Section */}
            <SectionCard title="Address Details" icon={<FaMapMarkerAlt />} description="Company location information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Address Line 1" name="address_line1" value={formData.address_line1.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter address line 1" required />
                <FormField label="Address Line 2" name="address_line2" value={formData.address_line2.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter address line 2" />
                <FormField label="City" name="city" value={formData.city.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter city" required />
                <FormField label="State" name="state" value={formData.state.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter state" required />
                <FormField label="Country" name="country" value={formData.country.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter country" required />
                <FormField label="Pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} disabled={!editMode} placeholder="Enter pincode" required />
              </div>
            </SectionCard>
          </div>
          
          {/* Right Column */}
          <div>
             {/* Inventory Settings Card */}
            <SectionCard title="Inventory Settings" icon={<FaBoxes />} description="Configure inventory management options">
              {/* <FormField label="Allow Negative Inventory" name="allow_negative_inventory" value={formData.allow_negative_inventory} onChange={handleInputChange} disabled={!editMode} placeholder="Allow inventory to go below zero" type="checkbox" icon={<FaBoxes />} />
              <FormField label="Allow Warehouse Negative Inventory" name="allow_warehouse_negative_inventory" value={formData.allow_warehouse_negative_inventory} onChange={handleInputChange} disabled={!editMode} placeholder="Maintain Negative Inventory Warehouse-wise" type="checkbox" icon={<FaWarehouse />} /> */}
              
              {/* VALIDATION LOGIC PRESERVED: Batch Duplicate Field */}
              <div className={`${company && company.AllowDuplicateBatch ? "opacity-60" : ""}`}>
                <FormField 
                  label="Allow Duplicate Batch" 
                  name="allow_duplicate_batch" 
                  value={formData.allow_duplicate_batch} 
                  onChange={handleInputChange} 
                  disabled={!editMode || (company && company.AllowDuplicateBatch)} 
                  placeholder="Allow duplicate batch numbers" 
                  type="checkbox" 
                  icon={<FaClone />} 
                />
                {company && company.AllowDuplicateBatch && (
                  <div className="ml-8 mt-2 text-xs text-orange-600 flex items-center font-medium">
                    <FaExclamationTriangle className="mr-1.5" />
                    This setting is active and cannot be changed.
                  </div>
                )}
              </div>
            </SectionCard>
            
            <SectionCard title="Registration Details" icon={<FaIdCard />} description="Legal and tax information">
              <FormField label="PAN Number" name="pan_number" value={formData.pan_number.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter PAN number" />
              <FormField label="CIN Number" name="cin_number" value={formData.cin_number.toUpperCase()} onChange={handleInputChange} disabled={!editMode} placeholder="Enter CIN number" />
              <FormField label="Registration Date" name="registration_date" value={formData.registration_date.toUpperCase()} onChange={handleInputChange} disabled={!editMode} icon={<FaCalendarAlt />} type="date" />
            </SectionCard>
            
            <SectionCard title="Additional Information" icon={<FaInfoCircle />} description="Other company details">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: colors.background }}>
                  <span className="text-sm font-medium" style={{ color: colors.secondary }}>Company ID</span>
                  <span className="font-mono" style={{ color: colors.primary }}>{formData.company_id}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: colors.background }}>
                  <span className="text-sm font-medium" style={{ color: colors.secondary }}>Last Updated</span>
                  <span style={{ color: colors.primary }}>Today</span>
                </div>
              </div>
            </SectionCard>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CompanyMaster;